'use client'

import { CheckCircle2, Shield, Target } from 'lucide-react'
import { motion } from 'motion/react'

import { talentPoolCopy } from '@/app/talent-pool/copy'

const SECURITY_ICONS = [Shield, CheckCircle2, Target] as const

export function TalentPoolSecuritySection() {
  const copy = talentPoolCopy

  return (
    <section id="keamanan" className="pb-14">
      <div className="rounded-3xl border border-border-primary bg-white dark:bg-card px-5 py-10 sm:px-8">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center gap-8 lg:flex-row lg:items-start lg:gap-12">
          {/* Left Column - Features */}
          <div className="w-full lg:flex-1">
            <div className="mb-6 flex flex-col items-center gap-4 text-center lg:items-start lg:text-left">
              <div className="rounded-full border-[1.5px] border-surface-primary bg-white dark:bg-card px-3 py-2 text-sm text-text-body">
                {copy.landing.navLinks[1].label}
              </div>
              <h2 className="text-[32px] font-medium leading-[40px] tracking-[-0.02em] text-text-heading md:text-[48px] md:leading-[60px] md:tracking-[-0.04em]">
                {copy.landing.security.title}{' '}
                <span className="inline-flex items-center bg-status-error-bg px-1">
                  {copy.landing.security.titleHighlight}
                </span>
              </h2>
            </div>

            <div className="space-y-4">
              {copy.landing.security.features.map((feature, index) => {
                const Icon = SECURITY_ICONS[index % SECURITY_ICONS.length]!
                return (
                  <motion.article
                    key={feature.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="flex gap-3 rounded-2xl border border-border-primary bg-white dark:bg-card p-5 transition-shadow hover:shadow-sm"
                  >
                    <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-primary text-text-heading">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-[16px] font-medium leading-[26px] text-text-heading md:text-[18px] md:leading-[28px]">
                        {feature.title}
                      </p>
                      <p className="mt-1 text-[14px] leading-[22px] text-text-muted md:text-[16px] md:leading-[26px]">
                        {feature.description}
                      </p>
                    </div>
                  </motion.article>
                )
              })}
            </div>
          </div>

          {/* Right Column - Ghost Mode Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6 }}
            className="w-full rounded-2xl border border-border-primary bg-gradient-to-br from-gray-900 to-gray-800 p-6 text-white shadow-lg dark:from-gray-950 dark:to-gray-900 lg:max-w-[380px] lg:mt-20"
          >
            <div className="mb-5 flex items-center justify-between border-b border-gray-700 pb-4">
              <p className="text-xs font-semibold tracking-wider text-gray-400">
                {copy.landing.security.panel.status}
              </p>
              <span className="relative inline-flex h-3 w-3">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-green-500" />
              </span>
            </div>
            <dl className="space-y-3 font-mono text-sm">
              {copy.landing.security.panel.rows.map((row, index) => (
                <motion.div
                  key={row.label}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className={`flex items-start justify-between gap-4 ${
                    row.hidden
                      ? 'text-gray-400'
                      : 'border-t border-gray-700 pt-3 font-semibold text-white'
                  }`}
                >
                  <dt className="text-gray-300">{row.label}</dt>
                  <dd
                    className={
                      row.hidden ? 'select-none blur-[4px] text-gray-500' : ''
                    }
                  >
                    {row.value}
                  </dd>
                </motion.div>
              ))}
            </dl>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
